/**
 * The ordered list of people who must sign a document.
 *
 * The list carries ONE invariant: `signer_order` is the row's position. In
 * sequential mode that order is who signs when, so a stale value would send
 * the document to the wrong person. It was re-established by hand in four
 * separate mutations (add, remove, edit, reorder); here every mutation goes
 * through `reindex`, so it cannot drift.
 *
 * The list is never empty — an empty signing form has nothing to fill in, so
 * removing the last row is a no-op rather than a disabled button that needs
 * explaining.
 */

import { useCallback, useState } from 'react';

/**
 * A row identifies its signer by account (`user_uuid`, docs' uuid of a
 * person of the install, with the `certificate_uuid` they sign with), by
 * national ID, or by both — the backend's `WorkflowParticipantCreate`.
 */
export const blankSigner = () => ({
  user_uuid: null,
  certificate_uuid: null,
  national_id_value: '',
  full_name: '',
  email: '',
  signer_order: 0,
});

/** PINFL — Uzbekistan's 14-digit personal identification number. */
export const isValidPinfl = (pinfl) => /^\d{14}$/.test(pinfl ?? '');

/** A signer's e-mail as the server takes it: something@somewhere, no spaces
 * (loose on purpose — accounts may live on private domains). Blank is fine. */
export const isValidSignerEmail = (email) => {
  const text = (email ?? '').trim();
  return !text || /^[^@\s]+@[^@\s]+$/.test(text);
};

/** The server's limits on a signer row (`workflow_participants`). */
export const SIGNER_NAME_MAX = 255;
export const SIGNER_EMAIL_MAX = 255;

/**
 * What is wrong with the filled rows, the first problem found:
 * `{problem: 'pinfl' | 'email', value}` or null. Rows naming nobody are
 * not checked (they are dropped).
 */
export function signerRowsProblem(rows) {
  for (const row of rows) {
    const pinfl = (row.national_id_value || '').trim();
    if (pinfl && !isValidPinfl(pinfl)) return { problem: 'pinfl', value: pinfl };
    if (!isValidSignerEmail(row.email)) return { problem: 'email', value: (row.email || '').trim() };
  }
  return null;
}

const reindex = (rows) => rows.map((row, index) => ({ ...row, signer_order: index }));

export function useSignerList() {
  const [signers, setSigners] = useState(() => [blankSigner()]);

  // The index currently being dragged, or null. Set on grip mousedown so a
  // drag can only start from the handle, not from anywhere in the row.
  const [draggedIndex, setDraggedIndex] = useState(null);

  const addSigner = useCallback(() => {
    setSigners((previous) => reindex([...previous, blankSigner()]));
  }, []);

  const removeSigner = useCallback((index) => {
    setSigners((previous) =>
      previous.length <= 1 ? previous : reindex(previous.filter((_, i) => i !== index)),
    );
  }, []);

  const updateSigner = useCallback((index, row) => {
    setSigners((previous) => reindex(previous.map((old, i) => (i === index ? row : old))));
  }, []);

  const moveSigner = useCallback((from, to) => {
    if (from == null || to == null || from === to) return;
    setSigners((previous) => {
      const next = [...previous];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return reindex(next);
    });
  }, []);

  const reset = useCallback(() => {
    setSigners([blankSigner()]);
    setDraggedIndex(null);
  }, []);

  /**
   * Rows the user actually filled in. A row counts once it names someone —
   * by account or by PINFL — so the trailing blank row a user leaves behind
   * is ignored rather than rejected.
   */
  const usableSigners = signers.filter(
    (row) => row.user_uuid != null || (row.national_id_value || '').trim(),
  );

  return {
    signers,
    setSigners,
    usableSigners,
    addSigner,
    removeSigner,
    updateSigner,
    moveSigner,
    reset,
    draggedIndex,
    setDraggedIndex,
  };
}
